// ============================================================
// WoodEstimator.js
// Single-screen Wood fence estimator.
//
// Mirrors VinylEstimator.js exactly: same navigation contract (returnTo /
// previousSpecs / priceLevel via location.state), same flat one-page card
// layout, same design tokens, and the same hand-off — on "Calculate & Add to
// Document" it runs calculateWood and navigates back to the originating
// document with { lineItems, estimatorSpecs, priceLevel }, the shape
// NewDocument/EditDocument already ingest generically.
//
// specs.kind = 'wood' is the discriminator, mirroring vinyl's 'vinyl'.
//
// Styles: Full Privacy, Shadowbox, Spaced Picket, Paddock, Dog Ear.
// Height availability is style-dependent and is clamped automatically so an
// impossible combination cannot be submitted.
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { calculateWood } from '../utils/woodCalc';

let runIdCounter = 1;
const newRun = () => ({ id: runIdCounter++, feet: '' });

// Style catalogue + the heights each style is built at.
const STYLES = [
  { value: 'fullPrivacy', label: 'Full Privacy' },
  { value: 'shadowbox', label: 'Shadowbox' },
  { value: 'spacedPicket', label: 'Spaced Picket' },
  { value: 'paddock', label: 'Paddock' },
  { value: 'dogEar', label: 'Dog Ear' },
];
const HEIGHTS_BY_STYLE = {
  fullPrivacy: ['4', '5', '6', '8'],
  shadowbox: ['4', '5', '6', '8'],
  spacedPicket: ['4', '5'],
  paddock: ['4', '5'],
  dogEar: ['6'],
};

// Default picket gap (inches) — mirrors the engine's own defaults.
const defaultGapFor = (style, profile) => {
  if (style === 'shadowbox') return profile === '1x6' ? 3.5 : 2.5;
  if (style === 'spacedPicket') return 2.5;
  return null;
};

export default function WoodEstimator({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { products } = useData();
  const returnTo = location?.state?.returnTo || null;
  const previousSpecs = location?.state?.previousSpecs || null;
  const docPriceLevel = location?.state?.priceLevel || 'A';
  const userName = user?.email?.split('@')[0]?.toUpperCase();

  // ── State (seeded from previousSpecs so "modify" pre-fills) ──
  const [style, setStyle] = useState(() => previousSpecs?.style || 'fullPrivacy');
  const [height, setHeight] = useState(() => previousSpecs?.height || '6');
  const [runs, setRuns] = useState(() => {
    if (previousSpecs?.runs?.length > 0) {
      // Reassign IDs to avoid collisions with the module-level counter.
      return previousSpecs.runs.map(r => ({ id: runIdCounter++, feet: r.feet ?? '' }));
    }
    return [newRun()];
  });
  const [corners, setCorners] = useState(() => previousSpecs?.corners ?? 0);
  const [ends, setEnds] = useState(() => previousSpecs?.ends ?? 0);
  const [picketProfile, setPicketProfile] = useState(() => previousSpecs?.picketProfile || '1x4');
  const [spacing, setSpacing] = useState(() => previousSpecs?.spacing ?? '');
  const [capBoard, setCapBoard] = useState(() => !!previousSpecs?.capBoard);
  const [fasciaBoard, setFasciaBoard] = useState(() => !!previousSpecs?.fasciaBoard);
  const [lattice, setLattice] = useState(() => !!previousSpecs?.lattice);
  const [paddockBoards, setPaddockBoards] = useState(() => String(previousSpecs?.paddockBoards ?? 3));
  const [weldedWire, setWeldedWire] = useState(() => !!previousSpecs?.weldedWire);
  const [hinge, setHinge] = useState(() => previousSpecs?.hinge || '8');
  const [latch, setLatch] = useState(() => previousSpecs?.latch || 'thumb');
  const [gates, setGates] = useState(() => previousSpecs?.gates || []);
  const [result, setResult] = useState(null); // { items, warnings } preview

  const runInputRefs = useRef({});

  // ── Derived capability flags ──
  const isPaddock = style === 'paddock';
  const isDogEar = style === 'dogEar';
  const usesPickets = !isPaddock;                       // paddock has no pickets
  const latticeAllowed = (style === 'fullPrivacy' || style === 'shadowbox') && height === '6';
  const latticeOn = lattice && latticeAllowed;
  // 1x6 is only offered on 6'/8' picket styles, and never when lattice forces 1x4.
  const profileSelectable = usesPickets && !isDogEar && !latticeOn && (height === '6' || height === '8');
  // The profile actually sent to the engine (it applies the same rules internally).
  const effectiveProfile = latticeOn ? '1x4'
    : isDogEar ? '1x6'
    : (height === '4' || height === '5') ? '1x4'
    : (picketProfile === '1x6' ? '1x6' : '1x4');
  const spacingApplies = style === 'shadowbox' || style === 'spacedPicket';
  const gapDefault = defaultGapFor(style, effectiveProfile);

  // ── Keep dependent fields legal when style/height change ──
  // Clamp the height to one the chosen style is built at, and drop options that
  // no longer apply, so an impossible combination can never be submitted.
  useEffect(() => {
    const allowed = HEIGHTS_BY_STYLE[style] || ['6'];
    if (!allowed.includes(height)) setHeight(allowed[0]);
  }, [style, height]);

  useEffect(() => {
    if (!latticeAllowed && lattice) setLattice(false);
  }, [latticeAllowed, lattice]);

  // Spacing follows the style/profile default until the user edits it.
  useEffect(() => {
    if (!spacingApplies) { setSpacing(''); return; }
    setSpacing(String(defaultGapFor(style, effectiveProfile)));
  }, [style, effectiveProfile, spacingApplies]);

  // Latch defaults by height (4' → gravity, otherwise thumb); still user-editable.
  useEffect(() => {
    setLatch(height === '4' ? 'gravity' : 'thumb');
  }, [height]);

  // Any spec change invalidates a previewed result.
  useEffect(() => { setResult(null); }, [
    style, height, runs, corners, ends, picketProfile, spacing,
    capBoard, fasciaBoard, lattice, paddockBoards, weldedWire, hinge, latch, gates,
  ]);

  // ── Run helpers (same pattern as VinylEstimator) ──
  const updateRun = (id, feet) => setRuns(prev => prev.map(r => r.id === id ? { ...r, feet } : r));
  const removeRun = (id) => { if (runs.length > 1) setRuns(prev => prev.filter(r => r.id !== id)); };
  const addRun = () => {
    const nr = newRun();
    setRuns(prev => [...prev, nr]);
    setTimeout(() => runInputRefs.current[nr.id]?.focus(), 50);
  };
  const handleRunKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addRun(); } };

  // ── Gate helpers (simple inline list — no modal) ──
  const addGate = () => setGates(prev => [...prev, { widthFeet: '4', double: false }]);
  const updateGate = (idx, field, value) => setGates(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  const removeGate = (idx) => setGates(prev => prev.filter((_, i) => i !== idx));

  // ── Specs — field names match woodCalc's contract exactly ──
  const buildSpecs = () => ({
    kind: 'wood', // discriminator so the correct "Modify" route can be chosen later
    style,
    height,
    runs,
    corners: parseInt(corners) || 0,
    ends: parseInt(ends) || 0,
    picketProfile: effectiveProfile,
    ...(spacingApplies && parseFloat(spacing) > 0 ? { spacing: parseFloat(spacing) } : {}),
    capBoard: usesPickets && !latticeOn ? capBoard : false,
    fasciaBoard: usesPickets ? fasciaBoard : false,
    lattice: latticeOn,
    ...(isPaddock ? { paddockBoards: parseInt(paddockBoards) || 3 } : {}),
    weldedWire: isPaddock ? weldedWire : false,
    hinge,
    latch,
    gates,
  });

  // ── Validation (safety net — the effects above prevent most of these) ──
  const issues = [];
  if (!(HEIGHTS_BY_STYLE[style] || []).includes(height)) {
    issues.push(`${STYLES.find(s => s.value === style)?.label} is not built at ${height}'.`);
  }
  if (lattice && !latticeAllowed) issues.push("Lattice is only available on 6' Full Privacy and Shadowbox.");
  if (picketProfile === '1x6' && usesPickets && !isDogEar && height !== '6' && height !== '8') {
    issues.push("1x6 pickets are only used at 6' and 8' — 4' and 5' use 1x4.");
  }
  const hasFootage = runs.some(r => parseFloat(r.feet) > 0);
  const canCalc = hasFootage && issues.length === 0;

  // ── Calculate ──
  const runCalc = () => {
    if (!products || products.length === 0) {
      alert('Products are still loading. Please wait a moment and try again.');
      return null;
    }
    return calculateWood(buildSpecs(), products, docPriceLevel);
  };

  const handlePreview = () => {
    const res = runCalc();
    if (res) setResult(res);
  };

  // Mirrors VinylEstimator.handleCalculate exactly.
  const handleCalculate = () => {
    if (returnTo && (!products || products.length === 0)) {
      alert('Products are still loading. Please wait a moment and try again.');
      return;
    }
    const specs = buildSpecs();
    if (returnTo) {
      const { items } = calculateWood(specs, products, docPriceLevel);
      navigate(returnTo, { state: { lineItems: items, estimatorSpecs: specs, priceLevel: docPriceLevel } });
    } else {
      // No standalone results page for wood — preview in place, then dashboard.
      navigate('/dashboard');
    }
  };

  // ── Derived ──
  const totalFootage = runs.reduce((s, r) => s + (parseFloat(r.feet) || 0), 0);

  // ── Shared inline styles (identical tokens to VinylEstimator) ──
  const cardStyle = { background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 };
  const sectionTitle = { color: '#03045E', fontWeight: 700, margin: '0 0 12px 0' };
  const labelStyle = { color: '#6B7C93', fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' };
  const inputStyle = {
    width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px',
    fontSize: 16, fontWeight: 700, color: '#03045E', background: '#FFFFFF', outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s',
  };
  const selectStyle = { ...inputStyle, fontSize: 14 };
  const focusOn = (e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; };
  const focusOff = (e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; };
  const noteStyle = { color: '#6B7C93', fontSize: 12, margin: '6px 0 0 0' };
  const checkRow = { display: 'flex', alignItems: 'center', gap: 8 };
  const checkLabel = (disabled) => ({ color: disabled ? '#9FB0C4' : '#03045E', fontSize: 14, fontWeight: 600 });

  // Style/height pill buttons — same treatment as the app's price-level pills.
  const pill = (active, disabled) => ({
    padding: '8px 14px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
    background: active ? '#0077B6' : '#FFFFFF',
    color: active ? '#FFFFFF' : (disabled ? '#9FB0C4' : '#0077B6'),
    border: `1px solid ${active ? '#0077B6' : '#E3ECF5'}`,
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <div className="min-h-screen" style={{ background: '#CDE7F5', padding: 20 }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* 1. Header */}
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
              <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>Wood Estimator</h1>
              <p style={{ color: '#6B7C93', fontSize: 13, margin: '2px 0 0 0' }}>
                {STYLES.find(s => s.value === style)?.label} · {height}'{userName ? ` · ${userName}` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* 2. Style & Height */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Style &amp; Height</p>
          <label style={labelStyle}>Style</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {STYLES.map(s => (
              <button key={s.value} type="button" onClick={() => setStyle(s.value)} style={pill(style === s.value, false)}>
                {s.label}
              </button>
            ))}
          </div>

          <label style={labelStyle}>Height</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {['4', '5', '6', '8'].map(h => {
              const allowed = (HEIGHTS_BY_STYLE[style] || []).includes(h);
              return (
                <button key={h} type="button" disabled={!allowed}
                  onClick={() => allowed && setHeight(h)}
                  style={pill(height === h, !allowed)}>
                  {h}'
                </button>
              );
            })}
          </div>
          {isDogEar && <p style={noteStyle}>Dog Ear is built at 6' only.</p>}
          {(isPaddock || style === 'spacedPicket') && <p style={noteStyle}>{STYLES.find(s => s.value === style)?.label} is built at 4' and 5' only.</p>}
        </div>

        {/* 3. Fence Layout */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Fence Layout</p>

          <div className="space-y-2" style={{ marginBottom: 8 }}>
            {runs.map((run, idx) => (
              <div key={run.id} className="flex items-center gap-2">
                <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Run {idx + 1}</span>
                <input
                  type="number"
                  value={run.feet === 0 ? '' : run.feet}
                  ref={el => { runInputRefs.current[run.id] = el; }}
                  onChange={e => updateRun(run.id, e.target.value)}
                  onKeyDown={handleRunKeyDown}
                  onFocus={e => { e.target.select(); focusOn(e); }}
                  onBlur={focusOff}
                  style={{ ...inputStyle, flex: 1, fontSize: 18 }}
                  placeholder="ft"
                  min="1"
                />
                <span style={{ color: '#6B7C93', fontSize: 12 }}>ft</span>
                {runs.length > 1 && (
                  <button onClick={() => removeRun(run.id)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontWeight: 700, padding: '0 4px', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>×</button>
                )}
              </div>
            ))}
          </div>
          <button onClick={addRun} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
            + Add Run
          </button>
          {totalFootage > 0 && (
            <div style={{ marginTop: 12, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 10, padding: 12 }}>
              <p style={{ color: '#03045E', fontWeight: 700, margin: 0 }}>Total: {totalFootage.toLocaleString()} ft</p>
              <p style={{ color: '#0077B6', fontSize: 12, margin: '2px 0 0 0' }}>{runs.filter(r => parseFloat(r.feet) > 0).length} run(s)</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3" style={{ marginTop: 16 }}>
            <div>
              <label style={labelStyle}>Corner Posts</label>
              <input
                type="number" min="0"
                value={corners === 0 ? '' : corners}
                onChange={e => setCorners(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={inputStyle} placeholder="0"
              />
            </div>
            <div>
              <label style={labelStyle}>End Posts</label>
              <input
                type="number" min="0"
                value={ends === 0 ? '' : ends}
                onChange={e => setEnds(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={inputStyle} placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* 4. Pickets & Options */}
        <div style={cardStyle}>
          <p style={sectionTitle}>{isPaddock ? 'Paddock Options' : 'Pickets & Options'}</p>

          {/* Picket profile — 1x6 only at 6'/8' on picket styles */}
          {usesPickets && !isDogEar && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Picket Profile</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['1x4', '1x6'].map(p => {
                  const disabled = p === '1x6' ? !profileSelectable : latticeOn || !profileSelectable;
                  const active = effectiveProfile === p;
                  return (
                    <button key={p} type="button" disabled={disabled}
                      onClick={() => !disabled && setPicketProfile(p)}
                      style={pill(active, disabled)}>
                      {p}
                    </button>
                  );
                })}
              </div>
              {latticeOn && <p style={noteStyle}>Lattice uses 1x4x5 pickets.</p>}
              {!latticeOn && (height === '4' || height === '5') && <p style={noteStyle}>4' and 5' fences always use 1x4 pickets.</p>}
            </div>
          )}
          {isDogEar && <p style={{ ...noteStyle, marginTop: 0, marginBottom: 16 }}>Dog Ear uses its own 5/8x6x6 board — no profile choice.</p>}
          {isPaddock && <p style={{ ...noteStyle, marginTop: 0, marginBottom: 16 }}>Paddock has no pickets — horizontal boards and faceboards only.</p>}

          {/* Spacing — shadowbox & spaced picket only */}
          {spacingApplies && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Picket Gap (inches)</label>
              <input
                type="number" min="0" step="0.25"
                value={spacing}
                onChange={e => setSpacing(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={{ ...inputStyle, maxWidth: 160 }}
                placeholder={String(gapDefault)}
              />
              <p style={noteStyle}>Default for {STYLES.find(s => s.value === style)?.label} {effectiveProfile}: {gapDefault}"</p>
            </div>
          )}

          {/* Paddock-only options */}
          {isPaddock && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Horizontal Boards per Section</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['3', '4'].map(n => (
                    <button key={n} type="button" onClick={() => setPaddockBoards(n)} style={pill(paddockBoards === n, false)}>
                      {n} boards
                    </button>
                  ))}
                </div>
              </div>
              <div style={checkRow}>
                <input type="checkbox" id="weldedWire" checked={weldedWire}
                  onChange={e => setWeldedWire(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                <label htmlFor="weldedWire" style={checkLabel(false)}>Welded wire backing</label>
              </div>
            </>
          )}

          {/* Privacy-style options */}
          {usesPickets && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {latticeAllowed && (
                <div style={checkRow}>
                  <input type="checkbox" id="lattice" checked={lattice}
                    onChange={e => setLattice(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                  <label htmlFor="lattice" style={checkLabel(false)}>Lattice top</label>
                </div>
              )}
              <div style={checkRow}>
                <input type="checkbox" id="capBoard" checked={capBoard && !latticeOn} disabled={latticeOn}
                  onChange={e => setCapBoard(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                <label htmlFor="capBoard" style={checkLabel(latticeOn)}>
                  Cap board{latticeOn ? ' — not used with lattice' : ''}
                </label>
              </div>
              <div style={checkRow}>
                <input type="checkbox" id="fasciaBoard" checked={fasciaBoard}
                  onChange={e => setFasciaBoard(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                <label htmlFor="fasciaBoard" style={checkLabel(false)}>Fascia board</label>
              </div>
            </div>
          )}
        </div>

        {/* 5. Gates */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Gates</p>
          {gates.length === 0 ? (
            <p style={{ color: '#6B7C93', fontSize: 13, margin: '0 0 8px 0' }}>No gates</p>
          ) : (
            <div className="space-y-2" style={{ marginBottom: 8 }}>
              {gates.map((gate, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Gate {i + 1}</span>
                  <input
                    type="number" min="1" step="0.5"
                    value={gate.widthFeet}
                    onChange={e => updateGate(i, 'widthFeet', e.target.value)}
                    onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                    style={{ ...inputStyle, flex: 1, fontSize: 14 }}
                    placeholder="ft"
                  />
                  <span style={{ color: '#6B7C93', fontSize: 12 }}>ft</span>
                  <select value={gate.double ? 'double' : 'single'}
                    onChange={e => updateGate(i, 'double', e.target.value === 'double')}
                    onFocus={focusOn} onBlur={focusOff} style={{ ...selectStyle, flex: 1 }}>
                    <option value="single">Single</option>
                    <option value="double">Double Pair</option>
                  </select>
                  <button onClick={() => removeGate(i)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <button onClick={addGate} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
            + Add Gate
          </button>
          <p style={noteStyle}>
            Double = total width of both leaves.
            {isPaddock ? ' Paddock gates use fixed arched pickets (15 per leaf).' : ''}
          </p>
        </div>

        {/* 6. Hardware */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Hardware</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Hinge</label>
              <select value={hinge} onChange={e => setHinge(e.target.value)} onFocus={focusOn} onBlur={focusOff} style={selectStyle}>
                <option value="8">T-Hinge 8" (pair)</option>
                <option value="10">T-Hinge 10" (pair)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Latch</label>
              <select value={latch} onChange={e => setLatch(e.target.value)} onFocus={focusOn} onBlur={focusOff} style={selectStyle}>
                <option value="gravity">Gravity Latch</option>
                <option value="thumb">Thumb Latch</option>
              </select>
            </div>
          </div>
          <p style={noteStyle}>Latch defaults to {height === '4' ? 'Gravity' : 'Thumb'} for a {height}' fence.</p>
        </div>

        {/* 7. Validation */}
        {issues.length > 0 && (
          <div style={{ ...cardStyle, background: '#FDF1DD', border: '1px solid #C99A33' }}>
            {issues.map((msg, i) => (
              <p key={i} style={{ color: '#854F0B', fontSize: 13, fontWeight: 600, margin: i === 0 ? 0 : '6px 0 0 0' }}>⚠️ {msg}</p>
            ))}
          </div>
        )}

        {/* 8. Materials preview */}
        {result && (
          <div style={cardStyle}>
            <p style={sectionTitle}>Materials ({result.items.length} lines)</p>
            {result.warnings.length > 0 && (
              <div style={{ background: '#FBECEC', border: '1px solid #E9C3C3', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <p style={{ color: '#A32D2D', fontSize: 13, fontWeight: 700, margin: 0 }}>
                  {result.warnings.length} SKU(s) not found in the price list:
                </p>
                <p style={{ color: '#A32D2D', fontSize: 12, margin: '4px 0 0 0' }}>{result.warnings.join(', ')}</p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {result.items.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 13 }}>
                  <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700, width: 90, flexShrink: 0 }}>{item.sku}</span>
                  <span style={{ color: item.found ? '#03045E' : '#A32D2D', flex: 1, minWidth: 0 }}>{item.description}</span>
                  <span style={{ color: '#03045E', fontWeight: 700, flexShrink: 0 }}>{item.qty} {item.unit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 9. Footer */}
        <div className="flex justify-between" style={{ marginTop: 4, gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(returnTo || '/dashboard')}
            style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 15, cursor: 'pointer' }}>
            Cancel
          </button>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={handlePreview}
              disabled={!canCalc}
              style={{
                background: '#FFFFFF', border: `1px solid ${canCalc ? '#0077B6' : '#E3ECF5'}`,
                color: canCalc ? '#0077B6' : '#6B7C93',
                fontWeight: 700, padding: '12px 24px', borderRadius: 12, fontSize: 15,
                cursor: canCalc ? 'pointer' : 'default',
              }}>
              Preview Materials
            </button>
            <button
              onClick={handleCalculate}
              disabled={!canCalc}
              style={{
                background: canCalc ? '#0077B6' : '#E3ECF5',
                border: 'none', color: canCalc ? '#FFFFFF' : '#6B7C93',
                fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 15,
                cursor: canCalc ? 'pointer' : 'default',
                boxShadow: canCalc ? '0 4px 14px rgba(0,119,182,0.3)' : 'none',
              }}>
              Calculate &amp; Add to Document
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
